import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKingdomRelationships } from '../api/dossier';
import type { KingdomRelationshipsResponse } from '../types/dossier';

// Module-level TTL cache: single source for Sidebar + Dossier header.
// Tests must reset this in beforeEach to avoid cross-test pollution (QA rec 11).
interface CacheEntry {
  data: KingdomRelationshipsResponse;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000; // 60 seconds

export function _resetKingdomRelationshipsCache() {
  cache.clear();
}

/**
 * Invalidates the cached relationships entry for one kingdom. War actions (declare/propose
 * peace/withdraw/cancel ceasefire) live on `Relations.tsx`, which holds a `useDiplomacy` instance
 * (a separate hook/cache backed by a different endpoint) — not a `useKingdomRelationships`
 * instance of its own — so there's no same-hook `refresh()` for the writer to call here. Consumers
 * of this hook (`Sidebar.tsx`'s war chips, `useStatusBanners.ts`'s At War/Ceasefire banner) are
 * different, separately-mounted components. Call this after a successful war action so their next
 * mount/fetch reflects the new state instead of serving up to 60s of stale cache.
 */
export function invalidateKingdomRelationships(worldId: string, kingdomId: string): void {
  cache.delete(`${worldId}:${kingdomId}`);
}

interface UseKingdomRelationshipsResult {
  data: KingdomRelationshipsResponse | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches WAR/HOSTILE relationships for a kingdom.
 *
 * Uses a module-level TTL cache (60s) shared between the Sidebar and Dossier header
 * to avoid redundant requests when both mount simultaneously.
 */
export function useKingdomRelationships(kingdomId: string | null): UseKingdomRelationshipsResult {
  const { worldId } = useAuth();
  const [data, setData] = useState<KingdomRelationshipsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRelationships = useCallback(async () => {
    if (!kingdomId || !worldId) {
      setData(null);
      return;
    }

    const cacheKey = `${worldId}:${kingdomId}`;
    const now = Date.now();
    const cached = cache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      setData(cached.data);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getKingdomRelationships(worldId, kingdomId);
      cache.set(cacheKey, { data: result, expiresAt: now + CACHE_TTL_MS });
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load relationships');
    } finally {
      setIsLoading(false);
    }
  }, [kingdomId, worldId]);

  useEffect(() => {
    fetchRelationships();
  }, [fetchRelationships]);

  return { data, isLoading, error };
}
